import { Button, Divider, Grid, Heading, HStack, ModalBody, ModalHeader, Select, Text, VStack } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import InputComponent from '@components/Input';
import { MdOutlineAdd, MdOutlineDelete } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { updateClientPayoutPartners } from '@store/actions/falconDashboardActions';
import { setAlert } from '@store/actions/alertActions';
import { ALERT_STATUS_TYPE } from '@constants';
import { formatNumberWithCommas } from '@utils';

const UpdatePayoutPartner = ({
   partners,
   onClose,
   fetchDetails,
   clientName,
   subClientName
}) => {
  const dispatch = useDispatch();
  const client = useSelector((state) => state.falconDashboard.client.data);
  const partnersList = useSelector(
    (state) => state.falconDashboard.config.data.payoutPartners
  );

  const [updatedPartners, setUpdatedPartners] = useState([]);
  const [newPartner, setNewPartner] = useState({
    partnerName: '',
    partnerBalance: '',
    retryCount: '',
    priority: '',
    minLimit: '',
    maxLimit: ''
  });

  const [toReview, setToReview] = useState(false);

  useEffect(() => {
    if (partners) {
      setUpdatedPartners(
        partners.map((partner) => ({
          partnerName: partner['partnerName']?.value,
          partnerBalance: partner['partnerBalance']?.value,
          retryCount: partner['retryCount']?.value,
          priority: partner['priority']?.value,
          minLimit: partner['minLimit']?.value,
          maxLimit: partner['maxLimit']?.value
        }))
      );
    }
  }, [partners]);

  const handleReview = () => {
    setToReview(!toReview);
  };

  const addPartner = () => {
    const partner = newPartner;
    if (!partner.partnerName || partner.partnerName.length === 0) {
      dispatch(
        setAlert({
          title: 'Partner name is required',
          status: ALERT_STATUS_TYPE.ERROR
        })
      );
      return;
    }

    if (
      !partner.retryCount ||
      isNaN(partner.retryCount) ||
      partner.retryCount < 0
    ) {
      dispatch(
        setAlert({
          title: 'Enter valid Retry Count',
          status: ALERT_STATUS_TYPE.ERROR
        })
      );
      return;
    }

    if (!partner.priority || isNaN(partner.priority) || partner.priority < 0) {
      dispatch(
        setAlert({
          title: 'Enter valid priority',
          status: ALERT_STATUS_TYPE.ERROR
        })
      );
      return;
    }

    if (
      partner.minLimit &&
      partner.maxLimit &&
      Number(partner.minLimit) > Number(partner.maxLimit)
    ) {
      dispatch(
        setAlert({
          title: 'Please enter valid minimum and maximum limits',
          status: ALERT_STATUS_TYPE.ERROR
        })
      );
      return;
    }

    const exists = updatedPartners.some(
      (obj) => obj.partnerName === partner.partnerName
    );

    setNewPartner({
      partnerName: '',
      partnerBalance: '',
      retryCount: '',
      priority: '',
      minLimit: '',
      maxLimit: ''
    });

    if (exists) {
      dispatch(
        setAlert({
          title: 'Payout partner already present',
          status: ALERT_STATUS_TYPE.ERROR
        })
      );
      return;
    }

    setUpdatedPartners((preState) => [...preState, partner]);
  };

  const handleRemove = (partner) => {
    setUpdatedPartners((prevState) =>
      prevState.filter((item) => item.partnerName !== partner)
    );
  };

  const handleUpdate = () => {
    const invalidLimitPartner = updatedPartners.find(
      (p) =>
        p.minLimit !== '' &&
        p.maxLimit !== '' &&
        Number(p.minLimit) > Number(p.maxLimit)
    );

    if (invalidLimitPartner) {
      dispatch(
        setAlert({
          title: `Min limit cannot be greater than max limit for partner "${invalidLimitPartner.partnerName}"`,
          status: ALERT_STATUS_TYPE.ERROR
        })
      );
      return;
    }

    const data = {
      client: client
        .filter((c) => c.heading === 'Client Details')[0]
        ?.data.filter((d) => d.title === 'Client Name')[0]?.value,
      currency: client
        .filter((c) => c.heading === 'Configurations')[0]
        ?.data.filter((d) => d.title === 'Currency')[0]?.value,
      subClient: subClientName,
      payoutPartners: updatedPartners
    };
    dispatch(
      updateClientPayoutPartners({
        data,
        onSuccess: fetchDetails
      })
    );

    onClose();
  };

  const handleChange = (e) => {
    setNewPartner((prevState) => ({
      ...prevState,
      partnerName: e.target.value
    }));
  };

  const handleUpdatePartner = (name, value, partnerName) => {
    const currentPartner = updatedPartners.find(
      (p) => p.partnerName === partnerName
    );
    if (!currentPartner) return;

    if (name === 'priority' || name === 'retryCount') {
      if (!Number.isInteger(Number(value)) || value < 0) {
        dispatch(
          setAlert({
            title: `${
              name === 'priority' ? 'Priority' : 'Retry count'
            } must be a valid integer`,
            status: ALERT_STATUS_TYPE.ERROR
          })
        );
        return;
      }
    }

    setUpdatedPartners((prevState) =>
      prevState.map((item) =>
        item.partnerName === partnerName ? { ...item, [name]: value } : item
      )
    );
  };

  return (
    <>
      <ModalHeader>Update Payout Partners Priority</ModalHeader>
      <ModalBody overflowX={'hidden'}>
        <VStack alignItems={'flex-start'} gap={'4'} px={'2'}>
          {clientName && subClientName && (
            <>
              <VStack align={'stretch'} gap={3}>
                <Heading size={'md'} color={'white'} mt={2}>
                  Current Selection
                </Heading>
                <HStack>
                  <Text fontSize={'sm'} color={'gray'} textAlign={'center'}>
                    Client Name:
                  </Text>
                  <Text
                    fontSize={'sm'}
                    color={'white'}
                    wordBreak={'break-word'}
                    mx={'auto'}
                  >
                    {clientName}
                  </Text>
                </HStack>
                <HStack>
                  <Text fontSize={'sm'} color={'gray'} textAlign={'center'}>
                    Sub Client Name:
                  </Text>
                  <Text
                    fontSize={'sm'}
                    color={'white'}
                    wordBreak={'break-word'}
                    mx={'auto'}
                  >
                    {subClientName}
                  </Text>
                </HStack>
              </VStack>
              <Divider />
            </>
          )}
          {!toReview && (
            <>
              <VStack alignItems={'stretch'} gap={'2'}>
                <Heading size={'sm'}>Add A New Priority</Heading>
                <Grid
                  w={'full'}
                  gridTemplateColumns={`2fr 1.5fr 1.5fr`}
                  alignItems={'center'}
                  justifyContent={'space-between'}
                  gap={4}
                >
                  {partnersList ? (
                    <>
                      <Select
                        name={'partnerName'}
                        placeholder={'Select Partner Name'}
                        value={newPartner.partnerName}
                        onChange={handleChange}
                        border={'none'}
                        borderRadius={'xl'}
                        backgroundColor={'#414141'}
                        focusBorderColor={'brand.400'}
                      >
                        {partnersList.map((partner) => (
                          <option value={partner}>{partner}</option>
                        ))}
                      </Select>
                      <InputComponent
                        name={'retryCount'}
                        placeholder={'Retry Count'}
                        width="100%"
                        handleChange={(e) =>
                          setNewPartner((prevState) => ({
                            ...prevState,
                            retryCount: e.target.value
                          }))
                        }
                        size={'md'}
                        value={newPartner.retryCount}
                      />
                      <InputComponent
                        name={'priority'}
                        placeholder={'Priority'}
                        width="100%"
                        handleChange={(e) =>
                          setNewPartner((prevState) => ({
                            ...prevState,
                            priority: e.target.value
                          }))
                        }
                        size={'md'}
                        value={newPartner.priority}
                      />
                      <InputComponent
                        name={'minLimit'}
                        placeholder={'Min Limit'}
                        width="100%"
                        handleChange={(e) =>
                          setNewPartner((prevState) => ({
                            ...prevState,
                            minLimit: e.target.value
                          }))
                        }
                        size={'md'}
                        value={newPartner.minLimit}
                      />
                      <InputComponent
                        name={'maxLimit'}
                        placeholder={'Max Limit'}
                        width="100%"
                        handleChange={(e) =>
                          setNewPartner((prevState) => ({
                            ...prevState,
                            maxLimit: e.target.value
                          }))
                        }
                        size={'md'}
                        value={newPartner.maxLimit}
                      />
                      <Button
                        w={'fit-content'}
                        borderRadius={'xl'}
                        colorScheme="brand"
                        color={'black'}
                        rightIcon={<MdOutlineAdd />}
                        onClick={addPartner}
                      >
                        Add
                      </Button>
                    </>
                  ) : (
                    <Text py={2} color={'whiteAlpha.900'}>
                      No Payout Partner Available
                    </Text>
                  )}
                </Grid>
              </VStack>
              <Divider />
            </>
          )}
          <VStack alignItems={'stretch'} gap={'2'} width={'100%'}>
            <Heading size={'sm'}>
              {!toReview ? 'Update Priority' : 'Review Priority'}
            </Heading>
            <VStack
              border={'2px solid'}
              borderColor={'whiteAlpha.500'}
              borderRadius={'xl'}
              width={'100%'}
            >
              <Grid
                gap={2}
                p={2}
                gridTemplateColumns={
                  !toReview
                    ? '1.5fr 1fr 1fr 1fr 1fr 1fr 1fr'
                    : '1.5fr 1fr 1fr 1fr 1fr'
                }
                alignItems={'center'}
                w={'full'}
                borderBottom={'2px solid'}
                borderColor={'whiteAlpha.500'}
              >
                <Text fontSize={'sm'} color={'gray'} textAlign={'center'}>
                  Partner Name
                </Text>
                {!toReview && (
                  <Text fontSize={'sm'} color={'gray'} textAlign={'center'}>
                    Balance
                  </Text>
                )}
                <Text fontSize={'sm'} color={'gray'} textAlign={'center'}>
                  Retry Count
                </Text>
                <Text fontSize={'sm'} color={'gray'} textAlign={'center'}>
                  Priority
                </Text>
                <Text fontSize={'sm'} color={'gray'} textAlign={'center'}>
                  Min Limit
                </Text>
                <Text fontSize={'sm'} color={'gray'} textAlign={'center'}>
                  Max Limit
                </Text>
                {!toReview && (
                  <Text fontSize={'sm'} color={'gray'} textAlign={'center'}>
                    Action
                  </Text>
                )}
              </Grid>
              <VStack py={2} width={'100%'}>
                {updatedPartners.length > 0 ? (
                  updatedPartners?.map((data, id) => (
                    <Grid
                      key={id}
                      gap={2}
                      p={2}
                      gridTemplateColumns={
                        !toReview
                          ? '1.5fr 1fr 1fr 1fr 1fr 1fr 1fr'
                          : '1.5fr 1fr 1fr 1fr 1fr'
                      }
                      alignItems={'center'}
                      w={'full'}
                    >
                      {toReview ? (
                        <>
                          <Text
                            fontSize={'xs'}
                            color={'white'}
                            wordBreak={'break-word'}
                          >
                            {data.partnerName}
                          </Text>
                          <Text
                            fontSize={'xs'}
                            color={'white'}
                            wordBreak={'break-word'}
                            mx={'auto'}
                          >
                            {data.retryCount}
                          </Text>
                          <Text
                            fontSize={'xs'}
                            color={'white'}
                            wordBreak={'break-word'}
                            mx={'auto'}
                          >
                            {data.priority}
                          </Text>
                          <Text
                            fontSize={'xs'}
                            color={'white'}
                            wordBreak={'break-word'}
                            mx={'auto'}
                          >
                            {data.minLimit
                              ? formatNumberWithCommas(
                                  parseFloat(data.minLimit)
                                )
                              : '-'}
                          </Text>
                          <Text
                            fontSize={'xs'}
                            color={'white'}
                            wordBreak={'break-word'}
                            mx={'auto'}
                          >
                            {data.maxLimit
                              ? formatNumberWithCommas(
                                  parseFloat(data.maxLimit)
                                )
                              : '-'}
                          </Text>
                        </>
                      ) : (
                        <>
                          <Text
                            fontSize={'xs'}
                            color={'white'}
                            wordBreak={'break-word'}
                            textAlign={'center'}
                          >
                            {data.partnerName}
                          </Text>
                          {data?.partnerBalance === null ? (
                            <Text
                              fontSize="xs"
                              color="white"
                              wordBreak="break-word"
                              textAlign="center"
                            >
                              -
                            </Text>
                          ) : data?.partnerBalance === '' ? (
                            <HStack justifyContent="center" alignItems="center">
                              <Text
                                px={1.5}
                                py={0.5}
                                borderRadius="xl"
                                bg="green.500"
                                fontSize="2xs"
                                color="white"
                                fontWeight="semibold"
                                textAlign="center"
                                lineHeight="1"
                              >
                                NEW
                              </Text>
                            </HStack>
                          ) : (
                            <Text
                              fontSize="xs"
                              color="white"
                              wordBreak="break-word"
                              textAlign="center"
                            >
                              {formatNumberWithCommas(data?.partnerBalance)}
                            </Text>
                          )}
                          <InputComponent
                            placeholder={'Retry Count'}
                            width="full"
                            size="sm"
                            handleChange={(e) =>
                              handleUpdatePartner(
                                e.target.name,
                                e.target.value,
                                data.partnerName
                              )
                            }
                            name={'retryCount'}
                            value={data.retryCount}
                          />
                          <InputComponent
                            placeholder={'Priority'}
                            width="full"
                            size="sm"
                            handleChange={(e) =>
                              handleUpdatePartner(
                                e.target.name,
                                e.target.value,
                                data.partnerName
                              )
                            }
                            name={'priority'}
                            value={data.priority}
                          />
                          <InputComponent
                            placeholder={'Min Limit'}
                            width="full"
                            size="sm"
                            handleChange={(e) =>
                              handleUpdatePartner(
                                e.target.name,
                                e.target.value,
                                data.partnerName
                              )
                            }
                            name={'minLimit'}
                            value={data.minLimit}
                          />
                          <InputComponent
                            placeholder={'Max Limit'}
                            width="full"
                            size="sm"
                            handleChange={(e) =>
                              handleUpdatePartner(
                                e.target.name,
                                e.target.value,
                                data.partnerName
                              )
                            }
                            name={'maxLimit'}
                            value={data.maxLimit}
                          />
                          <Button
                            width={'1rem'}
                            colorScheme={'red'}
                            size="sm"
                            bgColor="whiteAlpha.300"
                            border="1px solid"
                            borderColor="whiteAlpha.300"
                            borderRadius="full"
                            p={0}
                            mx={'auto'}
                            aria-label={`Delete ${client}`}
                            onClick={() => handleRemove(data.partnerName)}
                          >
                            <MdOutlineDelete size={'1rem'} />
                          </Button>
                        </>
                      )}
                    </Grid>
                  ))
                ) : (
                  <Text py={2} color={'whiteAlpha.900'}>
                    No Payout Partner Available
                  </Text>
                )}
              </VStack>
            </VStack>
          </VStack>
        </VStack>
        <HStack w="full" justifyContent={'flex-end'} my={4}>
          <Button
            colorScheme="brand"
            onClick={handleReview}
            variant={'outline'}
          >
            {toReview ? 'Edit' : 'Update'}
          </Button>
          {toReview && (
            <Button color={'black'} colorScheme="brand" onClick={handleUpdate}>
              Update
            </Button>
          )}
        </HStack>
      </ModalBody>
    </>
  );
};

export default UpdatePayoutPartner;
